// -- EVIDENCE COLLECTION VISIBILITY -- is collection actually working? (read-only) --------------
//
// ⚠ PER BINDING, NEVER PER CONTROL. MI's ruling: this answers "is VeriGRC collecting from this
// repository", not "how fresh is control IAM-001's evidence". A per-control freshness column here
// would duplicate the P1.4 surface and the two would eventually disagree.
//
// ⚠ READ-ONLY, AND THE ABSENCES ARE THE DESIGN. No "Run now", no retry, no cancel, no write path
// of any kind. The sweep's schedule is the sweep's.
//
// ⚠ NEVER A COUNTDOWN. The next-run value is a FLOOR the scheduler may legitimately slip past
// (throttling, PD-SWEEP-1), so it is phrased "on or after" with a "~". A hard "in 4h 12m" would be a
// promise the scheduler never made, and the first slip would read as a fault.
//
// ⚠ NEVER COLOUR ALONE. Each tier renders an icon AND words. Colour is decoration on top of a
// label that already says the thing.

import { Link } from 'react-router-dom';
import {
  AlertTriangle, CheckCircle2, CircleDashed, HelpCircle, Loader2, XCircle, Flag,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { useEvidenceCollectionStatus } from '@/api/hooks/useEvidenceCollectionStatus';
import type {
  BindingCollectionVisibility, CollectionStatus,
} from '@/api/types/evidence-collection.types';

/**
 * ⚠ THE FINDINGS BADGE POINTS AT THE CONTROLS WORKSPACE, NOT AT ONE CONTROL'S P1.4 PANEL.
 * P1.4 is per-control and this row is per-binding, so there is no single control to address: one
 * binding's findings can span many. This is the entry point from which each control's evidence tab
 * is reachable -- the nearest honest destination rather than an arbitrary one.
 */
export const FINDINGS_HREF = '/compliance/workspace/controls';

interface Presentation {
  label: string;
  Icon: typeof CheckCircle2;
  tone: string;
  /** ⚠ Only the two tiers that ASK FOR ACTION explain themselves. Healthy stays plain. */
  showReason: boolean;
}

/**
 * ⚠ EVERY TIER IS NAMED IN ITS OWN WORDS -- no raw server value ever reaches the screen.
 *
 * ⚠ AND `never_run` IS NOT GREEN. Collection has never happened; a tick here would be exactly
 * the dishonest surface this feature exists to remove. It is neutral, and it says so plainly.
 */
export const STATUS_PRESENTATION: Record<CollectionStatus, Presentation> = {
  running: { label: 'Collecting now', Icon: Loader2, tone: 'text-primary', showReason: false },
  healthy: { label: 'Collecting normally', Icon: CheckCircle2, tone: 'text-success', showReason: false },
  warning: { label: 'Needs attention', Icon: AlertTriangle, tone: 'text-warning', showReason: true },
  failed: { label: 'Not collecting', Icon: XCircle, tone: 'text-danger', showReason: true },
  never_run: { label: 'Not run yet', Icon: CircleDashed, tone: 'text-text-secondary', showReason: false },
};

/**
 * ⚠ AN UNRECOGNISED TIER IS NEUTRAL, NEVER GREEN AND NEVER A CRASH. The five tiers are the
 * frozen contract, but a client that has not been redeployed must not paint a state it has never
 * heard of as working -- nor throw on it and take the panel's other rows down with it.
 */
export const UNKNOWN_PRESENTATION: Presentation = {
  label: 'Status unavailable', Icon: HelpCircle, tone: 'text-text-secondary', showReason: true,
};

export const presentationFor = (status: string): Presentation =>
  STATUS_PRESENTATION[status as CollectionStatus] ?? UNKNOWN_PRESENTATION;

/** ⚠ NULL RATHER THAN A THROW OR "Invalid Date" -- one bad row must not poison the panel. */
export function absoluteTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * ⚠ DELIBERATELY COARSE. The value is an estimate floor, so the words are approximate too --
 * "about 3 days from now", never "in 2 days, 21 hours, 4 minutes". Precision the underlying number
 * does not have is a lie told in units.
 */
export function approximateRelative(iso: string | null, now: Date = new Date()): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const mins = Math.round((d.getTime() - now.getTime()) / 60000);
  const future = mins >= 0;
  const m = Math.abs(mins);
  const unit: readonly [number, string] = m < 60 ? [Math.max(m, 1), 'minute']
    : m < 60 * 24 ? [Math.round(m / 60), 'hour']
    : [Math.round(m / (60 * 24)), 'day'];
  const [n, word] = unit;
  const plural = n + ' ' + word + (n === 1 ? '' : 's');
  return future ? 'about ' + plural + ' from now' : 'about ' + plural + ' ago';
}

/**
 * ⚠ THE PLACEHOLDER MUST BE TRUE OF THE FIELD THAT IS MISSING, and this field is the last
 * SUCCESSFUL collection. "No runs yet" is only honest when the tier says nothing has run; a binding
 * whose every run failed HAS run, and telling its admin otherwise would hide those failures behind
 * a phrase that sounds like "nothing has happened yet".
 */
export function lastRunText(r: BindingCollectionVisibility): string {
  const at = absoluteTime(r.lastSuccessfulRun);
  if (at) return at;
  return r.status === 'never_run' ? 'No runs yet' : 'No successful collection yet';
}

function Field({ label, children, testId }: {
  label: string; children: React.ReactNode; testId: string;
}) {
  return (
    <p>
      <span className="text-text-secondary">{label}:</span>{' '}
      <span data-testid={testId}>{children}</span>
    </p>
  );
}

export function CollectionRow({ row }: { row: BindingCollectionVisibility }) {
  const p = presentationFor(row.status);
  const nextAbs = absoluteTime(row.nextScheduledRunEstimate);
  const nextRel = approximateRelative(row.nextScheduledRunEstimate);

  return (
    <div className="space-y-1 border-b border-border py-3 last:border-b-0"
         data-testid={'collection-row-' + row.bindingId}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className={cn('flex items-center gap-1.5 font-medium', p.tone)}
              data-testid={'collection-status-' + row.bindingId}>
          <p.Icon className={cn('h-4 w-4 shrink-0', row.status === 'running' && 'animate-spin')}
                  aria-hidden="true" />
          {p.label}
        </span>
        {row.displayName && <span className="text-text-secondary">{row.displayName}</span>}
        {/* ⚠ A SEPARATE BADGE, NOT A TIER. Findings mean the sweep WORKED and reported drift, so
            folding them into the status chip would downgrade a healthy binding for doing its job.
            Absent entirely when there are none -- an empty badge reads as "checked, nothing found",
            which is a claim this panel is not making. */}
        {row.hasFindings && (
          <Link to={FINDINGS_HREF}
                className="inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 text-xs underline"
                data-testid={'collection-findings-' + row.bindingId}>
            <Flag className="h-3 w-3" aria-hidden="true" />
            Changes found -- review evidence
          </Link>
        )}
      </div>

      {/* ⚠ THE REASON IS THE SERVER'S SENTENCE, and it appears only where the tier asks for action. */}
      {p.showReason && row.statusReason && (
        <p className="text-text-secondary" data-testid={'collection-reason-' + row.bindingId}>
          {row.statusReason}
        </p>
      )}

      <Field label="Last successful collection" testId={'collection-last-' + row.bindingId}>
        {lastRunText(row)}
      </Field>

      <Field label="Next collection" testId={'collection-next-' + row.bindingId}>
        {nextAbs
          ? <>on or after ~{nextAbs}{nextRel ? ' (~' + nextRel + ')' : ''}</>
          /* ⚠ NOT "soon", NOT a guessed date. Nothing is scheduled until the first sweep has
             something to schedule from, and saying so is the honest answer. */
          : 'Pending first sweep'}
      </Field>
    </div>
  );
}

export function EvidenceCollectionPanel() {
  const { data, isLoading, isError } = useEvidenceCollectionStatus();
  const rows = data ?? [];

  return (
    <section className="max-w-prose space-y-2 rounded border border-border p-4 text-sm"
             data-testid="evidence-collection">
      <h2 className="font-medium">Evidence collection</h2>
      <p className="text-text-secondary">
        Whether VeriGRC is reading from this repository on schedule. VeriGRC reads the location you
        designated; it never moves or deletes what is there.
      </p>

      {isLoading && <Skeleton className="h-20 w-full" data-testid="collection-loading" />}

      {/* ⚠ THE FAILURE IS LOCAL AND IT ASSERTS NOTHING. It says the status could not be read --
          not that collection is broken, and not that it is fine. The rest of the page, including the
          connection details the admin came for, renders regardless. */}
      {!isLoading && isError && (
        <p role="status" className="flex items-start gap-2 text-text-secondary"
           data-testid="collection-error">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          Evidence collection status could not be loaded. Nothing has changed -- try again shortly.
        </p>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <p className="text-text-secondary" data-testid="collection-empty">
          No repository is set up for evidence collection yet.
        </p>
      )}

      {!isLoading && !isError && rows.map((row) => (
        <CollectionRow key={row.bindingId} row={row} />
      ))}
    </section>
  );
}
